/**
 * Describing where a rejected module save differs from what is stored.
 *
 * @module components/studio/module-conflict-fields
 */

import { countLabel } from "@/components/ui";
import type { Module } from "@/types";
import { sameValue } from "@/utils/same-value";

/** One field the local draft and the stored module disagree on. */
export interface ModuleConflictField {
  /** The module property in question. */
  key: string;
  /** How the field is named to the person resolving it. */
  label: string;
  /** The local value, as it would be saved. */
  mine: unknown;
  /** The stored value, as another editor left it. */
  theirs: unknown;
}

/** Field names as the workspace already labels them, in the order it shows them. */
const FIELD_LABELS: Record<string, string> = {
  title: "Title",
  code: "Code",
  credits: "Credits",
  isElective: "Elective",
  stage: "Stage",
  semester: "Semester",
  moduleLeadName: "Module lead",
  moduleLeadEmail: "Module lead email",
  synopsis: "Synopsis",
  indicativeContent: "Indicative content",
  prerequisites: "Prerequisites",
  coRequisites: "Co-requisites",
  resourcesRequired: "Resources required",
  maximumLearners: "Maximum learners",
  staffQualifications: "Staff qualifications",
  mimlos: "Module outcomes",
  casDeviationNotes: "Source deviation notes",
  assessments: "Assessments",
  effortHours: "Effort hours",
  readingList: "Reading list",
};

/**
 * The fields a rejected save and the stored module both changed, differently.
 *
 * Deliberately not a merge. A silently merged module can be inconsistent in
 * ways neither author wrote: cutting credits 15 to 10 while the other adds
 * outcomes sized for 15 touches no common field. Collections are one field
 * each, because an item-level choice would be a merge in all but name. A
 * field only one side changed is left out, because the save composes it.
 *
 * @param mine - The local draft.
 * @param theirs - The stored module, or undefined when it was deleted.
 * @param base - The module the draft was read from.
 * @returns The differing fields, in the order the workspace shows them.
 */
export function moduleConflictFields(
  mine: Module,
  theirs: Module | undefined,
  base: Module,
): ModuleConflictField[] {
  if (!theirs) {
    return [];
  }
  return Object.keys(FIELD_LABELS)
    .filter(
      (key) =>
        !sameValue(mine[key], theirs[key]) &&
        !sameValue(mine[key], base[key]) &&
        !sameValue(theirs[key], base[key]),
    )
    .map((key) => ({ key, label: FIELD_LABELS[key], mine: mine[key], theirs: theirs[key] }));
}

/**
 * The base a draft saves against once the reader keeps it over a conflict.
 *
 * The server composes a save property by property against its base, so a
 * property both sides moved collides on every retry until its base is theirs.
 *
 * @param base - The module the draft was read from.
 * @param theirs - The module as stored, or undefined when it was deleted.
 * @param mine - The draft.
 * @returns The base with every property both sides moved taken from theirs.
 */
export function rebaseOnto(base: Module, theirs: Module | undefined, mine: Module): Module {
  if (!theirs) {
    return base;
  }
  const next: Module = { ...base };
  [...new Set([...Object.keys(mine), ...Object.keys(theirs)])]
    .filter((key) => !sameValue(mine[key], base[key]) && !sameValue(theirs[key], base[key]))
    .forEach((key) => {
      if (theirs[key] === undefined) {
        delete next[key];
      } else {
        next[key] = theirs[key];
      }
    });
  return next;
}

/** A programme's PLO-to-MIMLO map. */
type MappingMap = Record<string, string[]>;

/**
 * The MIMLO ids either copy of a module carries.
 *
 * @param copies - The draft and the stored module.
 * @returns Their outcome ids, so a placement of one is this module's.
 */
export function moduleMimloIds(...copies: (Module | undefined)[]): Set<string> {
  return new Set(copies.flatMap((copy) => (copy?.mimlos ?? []).map((mimlo) => mimlo.id)));
}

/**
 * The outcomes both the draft and the stored copy moved, and differently: the
 * grain the server refuses a mapping save on. An outcome's placement is each
 * programme outcome naming it and its position among this module's outcomes
 * there, so a reorder is a move.
 *
 * @param base - The map the draft was read from.
 * @param mine - The draft's map.
 * @param theirs - The stored map.
 * @param mimloIds - The module's outcome ids, in either copy.
 * @param savedMimloIds - The outcomes the draft still carries; a removed one
 *   takes its placements with it, so it collides with nothing.
 * @returns The colliding outcome ids.
 */
export function collidingMimloIds(
  base: MappingMap,
  mine: MappingMap,
  theirs: MappingMap,
  mimloIds: ReadonlySet<string>,
  savedMimloIds: ReadonlySet<string>,
): Set<string> {
  const placesOf = (map: MappingMap, mimloId: string): string =>
    Object.entries(map)
      .map(([ploId, ids]) => `${ploId}@${ids.filter((id) => mimloIds.has(id)).indexOf(mimloId)}`)
      .filter((place) => !place.endsWith("@-1"))
      .sort()
      .join();
  return new Set(
    [...savedMimloIds].filter((mimloId) => {
      const before = placesOf(base, mimloId);
      const after = placesOf(mine, mimloId);
      const now = placesOf(theirs, mimloId);
      return before !== after && before !== now && after !== now;
    }),
  );
}

/**
 * Take the stored placements of some outcomes into a map.
 *
 * Every other placement stays where the map has it, and a taken outcome goes
 * back in after the one stored before it, as the server places a placement it
 * keeps.
 *
 * @param mine - The map to take into.
 * @param theirs - The stored map.
 * @param taken - The outcome ids to take.
 * @returns The map with those outcomes placed as stored.
 */
export function takeModuleMappings(
  mine: MappingMap,
  theirs: MappingMap,
  taken: ReadonlySet<string>,
): MappingMap {
  const ploIds = new Set([...Object.keys(mine), ...Object.keys(theirs)]);
  return Object.fromEntries(
    [...ploIds].map((ploId) => {
      const placed = (mine[ploId] ?? []).filter((id) => !taken.has(id));
      let anchor = -1;
      (theirs[ploId] ?? []).forEach((id) => {
        const at = placed.indexOf(id);
        if (at >= 0) {
          anchor = at;
        } else if (taken.has(id)) {
          anchor += 1;
          placed.splice(anchor, 0, id);
        }
      });
      return [ploId, placed];
    }),
  );
}

/** The fields that name an outcome, an assessment or a reading. */
const ITEM_NAME_KEYS = ["text", "title", "citation"] as const;

/** Past this a summary stops being one line. */
const SUMMARY_LIMIT = 120;

function itemName(item: unknown): string | undefined {
  if (!item || typeof item !== "object") {
    return undefined;
  }
  const record = item as Record<string, unknown>;
  const name = ITEM_NAME_KEYS.map((key) => record[key]).find(
    (value) => typeof value === "string" && value.trim() !== "",
  );
  return typeof name === "string" ? name.trim() : undefined;
}

/** A count alone reads the same for two different collections of one size. */
function describeCollection(items: unknown[]): string {
  const count = countLabel(items.length, "item");
  const names = items.map(itemName).filter((name): name is string => Boolean(name));
  if (names.length === 0) {
    return count;
  }
  const summary = `${count}: ${names.join("; ")}`;
  return summary.length > SUMMARY_LIMIT ? `${summary.slice(0, SUMMARY_LIMIT - 1)}…` : summary;
}

/**
 * A short, readable rendering of a conflicting value.
 *
 * @param value - The value to describe.
 * @returns A one-line summary.
 */
export function describeConflictValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "Not set";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return describeCollection(value);
  }
  if (typeof value === "object") {
    return countLabel(Object.keys(value).length, "entry", "entries");
  }
  return String(value);
}

interface Difference {
  path: string[];
  mine: unknown;
  theirs: unknown;
}

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === "object" && value !== null;
}

/** The first leaf the two values disagree on, named the way a reader would. */
function firstDifference(
  mine: unknown,
  theirs: unknown,
  path: string[] = [],
): Difference | undefined {
  if (sameValue(mine, theirs)) {
    return undefined;
  }
  if (!isContainer(mine) || !isContainer(theirs) || Array.isArray(mine) !== Array.isArray(theirs)) {
    return { path, mine, theirs };
  }
  const mineEntries = mine as Record<string, unknown>;
  const theirEntries = theirs as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(mineEntries), ...Object.keys(theirEntries)])];
  let found: Difference | undefined;
  keys.some((key) => {
    const label = Array.isArray(mine)
      ? (itemName(mineEntries[key]) ?? `Item ${Number(key) + 1}`)
      : key;
    found = firstDifference(mineEntries[key], theirEntries[key], [...path, label]);
    return found !== undefined;
  });
  return found;
}

/**
 * Both sides of a conflicting field, told apart where their summaries alone
 * would read the same.
 *
 * @param mine - The local value.
 * @param theirs - The stored value.
 * @returns The two one-line summaries, mine first.
 */
export function describeConflictPair(mine: unknown, theirs: unknown): [string, string] {
  const summaries: [string, string] = [describeConflictValue(mine), describeConflictValue(theirs)];
  if (summaries[0] !== summaries[1]) {
    return summaries;
  }
  const difference = firstDifference(mine, theirs);
  if (!difference || difference.path.length === 0) {
    return summaries;
  }
  const where = difference.path.join(" ");
  return [
    `${summaries[0]} (${where}: ${describeConflictValue(difference.mine)})`,
    `${summaries[1]} (${where}: ${describeConflictValue(difference.theirs)})`,
  ];
}
